import { ReactElement, useCallback } from 'react';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { icon_home } from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  JabraIcon,
  useFallbackTranslation,
  withTheme,
} from '@gn-audio/jss-components';
import { getSharedProperty, SitecoreDictionaryItem } from '@gn-audio/jss-core';

import { useProductField } from 'common/PageProductContext/pageProductContext';
import { isBlankPageTitle } from 'common/PageProductContext/PageTitleUtils';

type IBreadcrumbProp = {
  breadcrumbsTitle: { jsonValue: { value: string } };
  breadcrumbsTitleDictionary: { jsonValue: SitecoreDictionaryItem };
  excludeFromBreadcrumbs: { value: '' | '1' };
  url: { path: string };
};

interface BreadcrumbsItem {
  item: IBreadcrumbProp & { ancestors: IBreadcrumbProp[] };
}

type BreadcrumbProps = ComponentProps & { fields: { data: BreadcrumbsItem } };

export const getBreadcrumbTitle = (item: IBreadcrumbProp): string | null =>
  item.breadcrumbsTitle?.jsonValue?.value !== ''
    ? item.breadcrumbsTitle.jsonValue.value
    : item.breadcrumbsTitleDictionary?.jsonValue?.fields?.Phrase?.value ||
      item.breadcrumbsTitleDictionary?.jsonValue?.fields?.PhraseShared?.value ||
      null;

const Breadcrumb = (props: BreadcrumbProps): ReactElement => {
  const { t } = useFallbackTranslation();
  const item = props.fields.data.item;
  const { productName } = useProductField();

  const items = [item, ...item.ancestors]
    .filter(
      (ancestor) =>
        !['1', 'True'].includes(getSharedProperty(ancestor, 'excludeFromBreadcrumbs')?.value || '')
    )
    .map((ancestor) => ({ title: getBreadcrumbTitle(ancestor), url: ancestor.url.path }))
    .filter((breadcrumb) => !!breadcrumb?.title || !!breadcrumb?.url)
    .reverse();

  const getCurrentPageTitle = useCallback(
    (breadcrumbTitle: string | null) => {
      return !isBlankPageTitle(breadcrumbTitle) ? breadcrumbTitle : productName;
    },
    [productName]
  );

  return (
    <div className="bg-theme dark relative h-10">
      <nav
        className="absolute bottom-0 z-10 inline-block translate-y-1/2 rounded-r-full bg-gray-800 py-4 pl-7 pr-12 text-gray-100"
        aria-label={t('system.Breadcrumbs')}
        role="navigation"
      >
        <ol className="flex h-full flex-wrap items-center">
          {items.map((breadcrumb, i) => {
            const isHome = i === 0;
            const isCurrentPage = i === items.length - 1;
            if (isHome) {
              return (
                <li key={breadcrumb.url} className="mr-6">
                  <Button
                    asLink
                    href={breadcrumb.url}
                    variant="quaternary"
                    size="m"
                    iconPlacement="right"
                    aria-label={t('system.Home')}
                    icon={<JabraIcon Icon={icon_home} />}
                  />
                </li>
              );
            }
            return (
              <li
                key={breadcrumb.url}
                className={`${i === items.length - 1 ? 'text-white' : 'text-gray-300'}`}
              >
                {i > 1 && (
                  <span className="mx-1" aria-hidden="true">
                    /
                  </span>
                )}
                {isCurrentPage ? (
                  <span aria-current="location">{getCurrentPageTitle(breadcrumb.title)}</span>
                ) : (
                  // todo: Replace back to Link after coexistence period
                  // todo: investigate why it comes with lang param already for non-local envs.
                  <a href={breadcrumb.url}>{breadcrumb.title}</a>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};

export default withTheme()(withDatasourceCheck()<BreadcrumbProps>(Breadcrumb));
